import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TypeAnimation } from "react-type-animation";

const BRAND_LOGO = "/logo-black.png";

// Lokal gehostet (public/images/landing/) — keine externen Bild-Requests (DSGVO: keine IP-Weitergabe an Dritt-Hosts)
const GYM_LOGOS = [
  { src: "/images/landing/gym-1.png", alt: "Power Athletics Gym" },
  { src: "/images/landing/gym-2.png", alt: "Fit Zone" },
  { src: "/images/landing/gym-3.png", alt: "Iron Works" },
  { src: "/images/landing/gym-4.png", alt: "Body Lab" },
  { src: "/images/landing/gym-5.png", alt: "Peak Fitness" },
  { src: "/images/landing/gym-6.png", alt: "Flex Studio" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");

  const handleGetStarted = (e) => {
    e.preventDefault();
    navigate("/registrieren", { state: { email } });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Navbar */}
      <nav data-testid="landing-navbar" className="bg-white/80 backdrop-blur-xl border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16">
          <a href="/" data-testid="landing-logo-link" className="flex items-center">
            <img src={BRAND_LOGO} alt="Performance Center" className="h-9 object-contain" />
          </a>
          <div className="flex items-center gap-4">
            <button
              data-testid="landing-login-link"
              onClick={() => navigate("/login")}
              className="text-sm font-semibold text-[#4B5563] hover:text-[#0A0A0A] transition-colors hidden sm:block"
            >
              Anmelden
            </button>
            <button
              data-testid="landing-join-btn"
              onClick={() => navigate("/registrieren")}
              className="bg-[#E60000] text-white hover:bg-[#CC0000] transition-colors shadow-lg hover:shadow-xl rounded-full px-6 py-2 text-sm font-bold tracking-wide uppercase"
            >
              Jetzt Starten
            </button>
          </div>
        </div>
        <div className="h-[3px] bg-gradient-to-r from-[#E60000] via-[#06B6D4] to-[#E60000]" />
      </nav>

      {/* Hero Section */}
      <section data-testid="landing-hero" className="min-h-[80vh] flex flex-col justify-center items-center text-center bg-white px-6">
        <h1 className="max-w-4xl mx-auto">
          <span
            className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl italic tracking-tight text-[#E60000] leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Du bist nur einen Klick entfernt von...
          </span>
          <span className="block mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#06B6D4]">
            <TypeAnimation
              sequence={[
                "einem ausgebuchten Gym",
                2500,
                "mehr Mitgliedern auf Autopilot",
                2500,
                "deinem Traum-Studio",
                2500,
                "automatisiertem Wachstum",
                2500,
              ]}
              wrapper="span"
              speed={40}
              repeat={Infinity}
              cursor={true}
            />
          </span>
        </h1>

        <p className="mt-8 text-lg text-[#4B5563]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Starte noch heute kostenlos.
        </p>

        {/* Email Capture */}
        <form
          data-testid="landing-email-form"
          onSubmit={handleGetStarted}
          className="mt-8 flex flex-col sm:flex-row items-center gap-3 w-full max-w-lg"
        >
          <input
            data-testid="landing-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Deine E-Mail-Adresse"
            className="flex-1 w-full border border-gray-200 bg-white focus:ring-2 focus:ring-[#E60000] focus:border-[#E60000] rounded-full px-6 py-3 outline-none transition-all text-base"
            required
          />
          <button
            data-testid="landing-get-started-btn"
            type="submit"
            className="bg-[#E60000] text-white hover:bg-[#CC0000] transition-colors shadow-lg hover:shadow-xl rounded-full px-8 py-3 text-sm font-bold tracking-wide uppercase whitespace-nowrap w-full sm:w-auto"
          >
            Loslegen
          </button>
        </form>
      </section>

      {/* Trusted By */}
      <section data-testid="landing-trusted-by" className="py-16 bg-[#F5F5F5] overflow-hidden border-y border-gray-200">
        <p className="text-center text-xs font-bold tracking-[0.2em] text-[#9CA3AF] mb-10 uppercase">
          Vertraut von
        </p>
        <div className="relative">
          <div className="flex animate-marquee gap-16 items-center">
            {[...GYM_LOGOS, ...GYM_LOGOS].map((logo, i) => (
              <img
                key={`logo-${logo.alt}-${i}`}
                src={logo.src}
                alt={logo.alt}
                className="h-16 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300 flex-shrink-0"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="landing-footer" className="py-8 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col sm:flex-row items-center justify-center gap-2 text-sm text-[#9CA3AF]">
          <span>Performance Center Eutingen - Alle Rechte vorbehalten &copy; 2024-2026</span>
          <span className="hidden sm:inline">|</span>
          <div className="flex items-center gap-2">
            <a href="/impressum" data-testid="footer-impressum-link" className="hover:text-[#0A0A0A] transition-colors">Impressum</a>
            <span>|</span>
            <a href="/datenschutz" data-testid="footer-datenschutz-link" className="hover:text-[#0A0A0A] transition-colors">Datenschutz</a>
            <span>|</span>
            <a href="/agb" data-testid="footer-agb-link" className="hover:text-[#0A0A0A] transition-colors">AGB</a>
            <span>|</span>
            <a href="/cookies" data-testid="footer-cookies-link" className="hover:text-[#0A0A0A] transition-colors">Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
